'use client';

import { useMemo } from 'react';
import { PrestationPaymentType } from '@cfixe-monorepo/shared';
import type { BookingDiscountSource } from '../../types';
import { hasNonZeroFormattedAmount, withFromPrefix } from '../../utils/payment-amounts';
import type { PaymentTimelineLabels } from './payment-timeline-labels';

type TimelineStepTone = 'accent' | 'success' | 'warning' | 'muted';

interface TimelineAmount {
  label: string;
  value: string;
  variant: 'refundable' | 'charged' | 'non-refundable' | 'remaining';
}

interface TimelineStep {
  key: string;
  title?: string;
  detail?: string;
  amounts?: TimelineAmount[];
  tone: TimelineStepTone;
  icon: string;
}

/** Champs de `payment_infos` lus par la timeline (sur-ensemble tolérant : booking + VTC). */
export interface PaymentTimelineInfos {
  amount_to_pay_today?: string;
  amount_to_pay_today_raw?: number;
  total_amount_raw?: number;
  deposit_amount?: string;
  remaining_amount_on_site?: string;
  discount_amount?: string;
  possible_refund_in_time?: string;
  possible_refund_out_time?: string;
  possible_refund_out_time_raw?: number;
  cancellation_fee_out_time?: string;
  cancellation_fee_out_time_raw?: number;
}

export interface PaymentTimelineProps {
  paymentType: string;
  paymentInfos?: PaymentTimelineInfos;
  /** ISO brut de la date limite d'annulation (pour tester si elle est passée). */
  cancellationDeadlineIso?: string;
  cancellationDeadlineFormatted?: string;
  /** Politique voucher snapshotée : coupon restitué avant la deadline ? (undefined = legacy = oui) */
  voucherRefundableOnCancel?: boolean;
  /**
   * Origine de `paymentInfos.discount_amount` (undefined = carte cadeau, comportement historique).
   * Une remise de code promo n'est pas un coupon : rien à restituer ni à perdre à l'annulation.
   */
  discountSource?: BookingDiscountSource;
  /** Panier à prix « plancher » (QUOTE/FROM) → préfixe « à partir de ». Faux hors booking. */
  hasLowerBoundPricing?: boolean;
  /** Panier « aucun prix » (NONE) → « Prix à déterminer sur place ». Faux hors booking. */
  hasNoPricePricing?: boolean;
  labels: PaymentTimelineLabels;
}

/**
 * Timeline de paiement/remboursement (composant PARTAGÉ booking classique ↔ VTC) : ne calcule
 * aucun montant, elle ré-étiquette et branche sur `paymentType` en lisant `paymentInfos`. Toute la
 * donnée (déjà formatée) vient des props — voir `BookingPaymentTimeline` (adaptateur context) et le
 * tunnel VTC (props issues du devis `RidePriceCalculatorResponse`).
 */
export function PaymentTimeline({
  paymentType,
  paymentInfos,
  cancellationDeadlineIso,
  cancellationDeadlineFormatted,
  voucherRefundableOnCancel,
  discountSource,
  hasLowerBoundPricing = false,
  hasNoPricePricing = false,
  labels,
}: PaymentTimelineProps): React.JSX.Element | null {
  const steps = useMemo((): TimelineStep[] => {
    const infos = paymentInfos;
    if (!infos) return [];

    const amountToPayToday = infos.amount_to_pay_today;
    const amountToPayTodayRaw = infos.amount_to_pay_today_raw ?? 0;
    const totalAmountRaw = infos.total_amount_raw ?? 0;
    const remainingOnSite = infos.remaining_amount_on_site;
    const voucherDiscount = infos.discount_amount;
    const refundIn = infos.possible_refund_in_time;
    const refundOut = infos.possible_refund_out_time;
    const refundOutRaw = infos.possible_refund_out_time_raw ?? 0;
    const cancellationFeeOut = infos.cancellation_fee_out_time;
    const cancellationFeeOutRaw = infos.cancellation_fee_out_time_raw ?? 0;
    const hasRemaining = remainingOnSite
      ? hasNonZeroFormattedAmount(remainingOnSite)
      : totalAmountRaw - amountToPayTodayRaw > 0;
    const deadlineIso = cancellationDeadlineIso;
    const deadlineFormatted = cancellationDeadlineFormatted;
    const deadlinePassed = deadlineIso ? new Date(deadlineIso).getTime() <= Date.now() : true;

    const isImprint = paymentType === PrestationPaymentType.BANK_IMPRINT;
    const isCardSave = paymentType === PrestationPaymentType.CARD_SAVE;
    const isDeposit = paymentType === PrestationPaymentType.DEPOSIT;
    // Remise de code promo : déjà déduite des montants, elle ne donne lieu à aucune ligne « coupon ».
    const hasVoucherDiscount = discountSource !== 'promo' && hasNonZeroFormattedAmount(voucherDiscount);
    const isFullyCoveredByVoucher = !isImprint && !isCardSave && hasVoucherDiscount && amountToPayTodayRaw === 0;
    const isFree =
      paymentType === PrestationPaymentType.FREE ||
      (amountToPayTodayRaw === 0 && !isImprint && !isCardSave && !hasVoucherDiscount);

    /* ── No-payment shortcut ─────────────────── */
    if (isFree) {
      const simpleStep: TimelineStep = {
        key: 'cancel-window',
        title: undefined,
        detail: deadlinePassed
          ? labels.timeline_attendance_commitment
          : `${labels.timeline_cancel_before} ${String(deadlineFormatted ?? '')}`,
        tone: 'muted',
        icon: '○',
      };
      const dayOfStep: TimelineStep = {
        key: 'day-of',
        title: labels.timeline_day_of,
        detail: hasRemaining
          ? labels.timeline_pay_on_site
          : hasNoPricePricing
            ? labels.timeline_price_to_determine_on_site
            : labels.timeline_nothing_on_site,
        amounts:
          hasRemaining && remainingOnSite
            ? [
                {
                  label: labels.timeline_amount_remaining,
                  value: withFromPrefix(String(remainingOnSite), hasLowerBoundPricing, labels.from_price_prefix),
                  variant: 'remaining',
                },
              ]
            : undefined,
        tone: 'muted',
        icon: '📅',
      };
      return [simpleStep, dayOfStep];
    }

    /* ── Step 1: now ─────────────────────────── */
    const stepNow: TimelineStep = ((): TimelineStep => {
      if (isImprint) {
        return {
          key: 'now',
          title: labels.timeline_now,
          detail: labels.timeline_imprint_held,
          tone: 'accent',
          icon: '🔒',
        };
      }
      if (isCardSave) {
        return {
          key: 'now',
          title: labels.timeline_now,
          detail: labels.timeline_card_saved,
          tone: 'accent',
          icon: '💳',
        };
      }
      if (isFullyCoveredByVoucher) {
        return {
          key: 'now',
          title: labels.timeline_now,
          detail: labels.voucher_info_applied_generic,
          tone: 'accent',
          icon: '+',
        };
      }
      return {
        key: 'now',
        title: labels.timeline_now,
        detail: isDeposit ? labels.timeline_deposit_charged : labels.timeline_full_charged,
        amounts: amountToPayToday
          ? [{ label: labels.timeline_amount_charged, value: String(amountToPayToday), variant: 'charged' }]
          : undefined,
        tone: 'accent',
        icon: '💳',
      };
    })();

    /* ── Step 2: cancellation window ─────────── */
    const refundableAmountsBeforeDeadline: TimelineAmount[] = [];
    if (!isImprint && !isCardSave && !isFree && refundIn && hasNonZeroFormattedAmount(refundIn)) {
      refundableAmountsBeforeDeadline.push({
        label: labels.timeline_amount_refundable,
        value: String(refundIn),
        variant: 'refundable',
      });
    }
    if (hasVoucherDiscount && voucherDiscount) {
      // Politique pro snapshotée : undefined = legacy = coupon restitué avant la deadline.
      const voucherRefundable = voucherRefundableOnCancel !== false;
      refundableAmountsBeforeDeadline.push({
        label: labels.timeline_voucher_label,
        value: voucherRefundable ? labels.timeline_voucher_refund : labels.timeline_voucher_no_refund,
        variant: voucherRefundable ? 'refundable' : 'non-refundable',
      });
    }

    const stepFreeCancel: TimelineStep | null = deadlinePassed
      ? null
      : {
          key: 'free-cancel',
          title: labels.timeline_until,
          detail: `${
            isImprint || isCardSave || isFree ? labels.timeline_no_charge_until : labels.timeline_free_cancel_until
          } ${String(deadlineFormatted ?? '')}`,
          amounts: refundableAmountsBeforeDeadline.length > 0 ? refundableAmountsBeforeDeadline : undefined,
          tone: 'success',
          icon: '↩︎',
        };

    /* ── Step 3: post-deadline ───────────────── */
    const afterTitle = deadlinePassed
      ? labels.timeline_now_no_cancel
      : `${labels.timeline_after_deadline} ${String(deadlineFormatted ?? '')}`;
    const stepAfter: TimelineStep = ((): TimelineStep => {
      if (isImprint) {
        return {
          key: 'after',
          title: afterTitle,
          detail:
            cancellationFeeOutRaw > 0 ? labels.timeline_imprint_chargeable : labels.timeline_no_charge_after_deadline,
          amounts:
            cancellationFeeOutRaw > 0 && cancellationFeeOut
              ? [
                  {
                    label: labels.timeline_amount_cancellation_fee,
                    value: String(cancellationFeeOut),
                    variant: 'non-refundable',
                  },
                ]
              : undefined,
          tone: 'warning',
          icon: '⚠️',
        };
      }
      if (isCardSave) {
        return {
          key: 'after',
          title: afterTitle,
          detail:
            cancellationFeeOutRaw > 0
              ? labels.timeline_cancellation_fee_can_apply
              : labels.timeline_no_charge_after_deadline,
          amounts:
            cancellationFeeOutRaw > 0 && cancellationFeeOut
              ? [
                  {
                    label: labels.timeline_amount_cancellation_fee,
                    value: String(cancellationFeeOut),
                    variant: 'non-refundable',
                  },
                ]
              : undefined,
          tone: 'warning',
          icon: '⚠️',
        };
      }
      if (isFree) {
        return {
          key: 'after',
          title: afterTitle,
          detail: labels.timeline_no_charge_after_deadline,
          tone: 'warning',
          icon: '⚠️',
        };
      }
      const hasPartialRefund = refundOutRaw > 0;
      const afterAmounts: TimelineAmount[] = [];
      if (hasVoucherDiscount && voucherDiscount) {
        afterAmounts.push({
          label: labels.timeline_voucher_label,
          value: labels.timeline_voucher_no_refund,
          variant: 'non-refundable',
        });
      }
      // Correction: en mode acompte, après la deadline, il ne doit jamais y avoir de remboursement affiché
      if (isDeposit) {
        const depositAmounts: TimelineAmount[] = [];
        if (amountToPayToday && hasNonZeroFormattedAmount(amountToPayToday)) {
          depositAmounts.push({
            label: labels.timeline_amount_non_refundable,
            value: String(amountToPayToday),
            variant: 'non-refundable',
          });
        }
        depositAmounts.push(...afterAmounts);

        return {
          key: 'after',
          title: afterTitle,
          detail: labels.timeline_non_refundable,
          amounts: depositAmounts.length > 0 ? depositAmounts : undefined,
          tone: 'warning',
          icon: '⚠️',
        };
      }
      if (!hasPartialRefund && amountToPayToday && hasNonZeroFormattedAmount(amountToPayToday)) {
        afterAmounts.push({
          label: labels.timeline_amount_non_refundable,
          value: String(amountToPayToday),
          variant: 'non-refundable',
        });
      }
      return {
        key: 'after',
        title: afterTitle,
        detail: hasPartialRefund
          ? `${String(refundOut)} ${labels.timeline_partial_refund_after}`
          : labels.timeline_non_refundable,
        amounts: afterAmounts.length > 0 ? afterAmounts : undefined,
        tone: 'warning',
        icon: '⚠️',
      };
    })();

    /* ── Step 4: day of appointment ──────────── */
    const stepDayOf: TimelineStep | null = isImprint
      ? {
          key: 'day-of',
          title: labels.timeline_day_of,
          detail: labels.timeline_imprint_released,
          tone: 'muted',
          icon: '📅',
        }
      : {
          key: 'day-of',
          title: labels.timeline_day_of,
          detail: hasRemaining
            ? labels.timeline_pay_on_site
            : hasNoPricePricing
              ? labels.timeline_price_to_determine_on_site
              : labels.timeline_nothing_on_site,
          amounts:
            hasRemaining && remainingOnSite
              ? [
                  {
                    label: labels.timeline_amount_remaining,
                    value: withFromPrefix(String(remainingOnSite), hasLowerBoundPricing, labels.from_price_prefix),
                    variant: 'remaining',
                  },
                ]
              : undefined,
          tone: 'muted',
          icon: '📅',
        };

    return [stepNow, stepFreeCancel, stepAfter, stepDayOf].filter((s): s is TimelineStep => s !== null);
  }, [
    paymentType,
    paymentInfos,
    cancellationDeadlineIso,
    cancellationDeadlineFormatted,
    voucherRefundableOnCancel,
    discountSource,
    labels,
    hasLowerBoundPricing,
    hasNoPricePricing,
  ]);

  if (steps.length === 0) return null;

  return (
    <div className="bt-payment-timeline" role="list" aria-label={labels.timeline_title}>
      <div className="bt-payment-timeline__title">{labels.timeline_title}</div>
      <ol className="bt-payment-timeline__track">
        {steps.map(
          (step): React.JSX.Element => (
            <li
              key={step.key}
              className={`bt-payment-timeline__step bt-payment-timeline__step--${step.tone}`}
              role="listitem"
            >
              <div className="bt-payment-timeline__marker">
                <span className="bt-payment-timeline__icon" aria-hidden="true">
                  {step.icon}
                </span>
              </div>
              <div className="bt-payment-timeline__content">
                {step.title ? <div className="bt-payment-timeline__step-title">{step.title}</div> : null}
                {step.detail && <div className="bt-payment-timeline__step-detail">{step.detail}</div>}
                {step.amounts?.map(
                  (a, j): React.JSX.Element => (
                    <div
                      key={`${step.key}-amt-${j}`}
                      className={`bt-payment-timeline__amount bt-payment-timeline__amount--${a.variant}`}
                    >
                      <span className="bt-payment-timeline__amount-label">{a.label}</span>
                      <span className="bt-payment-timeline__amount-value">{a.value}</span>
                    </div>
                  ),
                )}
              </div>
            </li>
          ),
        )}
      </ol>
    </div>
  );
}
